import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Cross, Menu, X, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";

const publicLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/bible", label: "Bible" },
  { to: "/contact", label: "Contact" },
];

const privateLinks = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/sin-tracker", label: "Sin Tracker" },
  { to: "/chat", label: "AI Companion" },
  { to: "/prayer", label: "Prayer Journal" },
  { to: "/analytics", label: "Analytics" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = user ? privateLinks : publicLinks;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-brand-line/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-sky to-brand-sage grid place-items-center text-white shadow-sm">
            <Cross className="w-4 h-4" strokeWidth={1.8} />
          </span>
          <span className="font-serif text-2xl tracking-tight text-brand-ink">
            Path to Repentance
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-full transition-colors ${
                  isActive
                    ? "text-brand-ink bg-brand-mist"
                    : "text-brand-quiet hover:text-brand-ink hover:bg-brand-mist/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className="inline-flex items-center gap-1.5 text-sm text-brand-quiet hover:text-brand-ink"
                >
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.6} /> Admin
                </Link>
              )}
              <span className="text-sm text-brand-quiet hidden lg:inline">
                {user.anonymous_mode ? "Anonymous" : user.name}
              </span>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-mist text-brand-ink hover:bg-brand-line transition-colors px-4 py-2 text-sm font-medium"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.6} /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="rounded-full px-4 py-2 text-sm text-brand-ink hover:bg-brand-mist transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-link"
                className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"
              >
                <LayoutDashboard className="w-4 h-4" strokeWidth={1.6} /> Begin journey
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-full hover:bg-brand-mist"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-brand-line bg-white/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-sm ${
                    isActive ? "bg-brand-mist text-brand-ink" : "text-brand-quiet"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-brand-line my-2" />
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-admin-link"
                    className="px-3 py-2.5 rounded-lg text-sm text-brand-ink"
                  >
                    Admin panel
                  </Link>
                )}
                <button
                  onClick={() => {
                    setOpen(false);
                    handleLogout();
                  }}
                  data-testid="mobile-logout-button"
                  className="text-left px-3 py-2.5 rounded-lg text-sm text-brand-ink"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-login-link"
                  className="px-3 py-2.5 rounded-lg text-sm text-brand-ink"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-register-link"
                  className="px-3 py-2.5 rounded-lg text-sm bg-brand-sky text-white text-center"
                >
                  Begin journey
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
